import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  adminUserRequest, counterSignCollaborativeAgreement, CounterSignCollaborativeAgreementParams,
  createCollaborativePhysicianMatch, CreateCollaborativePhysicianParams, deleteCollaborativePhysicianMatch,
  DeleteCollaborativePhysicianParams, getAllCollaborativePhysicians, getCollaborativePhysicianMatchAgreement,
  getCollaborativePhysicianMatchesForPhysician, getCollaborativePhysicianMatchesForProvider, isApiError,
  signCollaborativeAgreement, SignCollaborativeAgreementParams, getCollaborativePhysicianMatch
} from '@uplift/api';
import sortBy from 'lodash/sortBy';


export const useAllCollaborativePhysiciansQuery = () => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician' ],
    queryFn: async () => {
      const collaborativePhysiciansResponse = await getAllCollaborativePhysicians();
      const collaborativePhysicians = await Promise.all(
        collaborativePhysiciansResponse.data.map(async item => ({
          supportedStateIds: item.supportedStateIds,
          ...(await adminUserRequest(item.providerId)),
        }))
      );

      const users = collaborativePhysicians.map(u => ({
        id: u.id,
        name: u.user.firstName + ' ' + u.user.lastName,
        email: u.user.email,
        supportedStateIds: u.supportedStateIds,
      }));

      return sortBy(users, 'createdAt').reverse();
    }
  });
};


export const useCollaboratePhysicianMatchesQuery = (collaborativePhysicianId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', collaborativePhysicianId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForPhysician(collaborativePhysicianId);

      return response.data;
    },
  });
};

export const useCollaboratePhysicianMatchesForProviderQuery = (providerId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', providerId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForProvider(providerId);

      return response.data;
    },
  });
};

export const useCollaboratePhysicianMatchQuery = (matchId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', matchId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatch(matchId);

      return response.data;
    },
  });
};

export const useCollaboratePhysicianMatchForProviderAndStateQuery = (providerId: string, stateId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', providerId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForProvider(providerId);

      return response.data;
    },
    select: (matches) => matches.find(m => m.stateId === stateId),
  });
};

export const useCollaboratePhysicianMatchByIdForProviderQuery = (providerId: string, matchId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', providerId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForProvider(providerId);

      return response.data;
    },
    select: (matches) => matches.find(m => m.id === matchId),
  });
};

export const useCollaboratePhysicianMatchByIdForPhysicianQuery = (collaborativePhysicianId: string, matchId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'matches', collaborativePhysicianId ],
    queryFn: async () => {
      const response = await getCollaborativePhysicianMatchesForPhysician(collaborativePhysicianId);

      return response.data;
    },
    select: (matches) => matches.find(m => m.id === matchId),
  });
};


export const useCollaboratePhysicianMatchAgreementQuery = (matchId: string) => {
  return useSuspenseQuery({
    queryKey: [ 'collaborativePhysician', 'match', matchId, 'agreement' ],
    queryFn: async () => {
      try {
        const response = await getCollaborativePhysicianMatchAgreement(matchId);

        return response.data;
      } catch {
        return null;
      }
    },
  });
};


export const useCreateCollaborativePhysicianMatchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: CreateCollaborativePhysicianParams) => {
      await createCollaborativePhysicianMatch(values);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'collaborativePhysician' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error_key === 'seat_capacity_reached') {
          return;
        }
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while adding match');
        }
      } else {
        throw new Error('An error occurred while adding match');
      }
    },
  });
};


export const useDeleteCollaborativePhysicianMatchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: DeleteCollaborativePhysicianParams) => {
      await deleteCollaborativePhysicianMatch(values);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'collaborativePhysician' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while deleting match');
        }
      } else {
        throw new Error('An error occurred while deleting match');
      }
    },
  });
};


export const useSignCollaborativeAgreementMutation = (providerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: SignCollaborativeAgreementParams) => {
      const response = await signCollaborativeAgreement(values);

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'user', providerId, 'documents' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while signing the agreement');
        }
      } else {
        throw new Error('An error occurred while signing the agreement');
      }
    },
  });
};


export const useCounterSignCollaborativeAgreementMutation = (physicianId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: CounterSignCollaborativeAgreementParams) => {
      const response = await counterSignCollaborativeAgreement(values);

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'user', physicianId, 'documents' ] });
    },
    onError: async (error) => {
      if (isApiError(error)) {
        if (error?.response?.data.error) {
          throw new Error(error.response.data.error);
        } else {
          throw new Error('An error occurred while counter-signing the agreement');
        }
      } else {
        throw new Error('An error occurred while counter-signing the agreement');
      }
    },
  });
};
